import { PublicKey, Transaction, TransactionInstruction } from '@solana/web3.js';
import {
  getInstructionDiscriminator,
  getMayhemProgram,
} from '../program/mayhemProgram';

function deriveAssociatedTokenAddress(owner, mint, associatedTokenProgramId, tokenProgramId) {
  const [publicKey] = PublicKey.findProgramAddressSync(
    [owner.toBuffer(), tokenProgramId.toBuffer(), mint.toBuffer()],
    associatedTokenProgramId
  );

  return publicKey;
}

function encodeU64(value) {
  const buffer = new Uint8Array(8);
  let remainingValue = value;

  for (let index = 0; index < 8; index += 1) {
    buffer[index] = Number(remainingValue & 255n);
    remainingValue >>= 8n;
  }

  return buffer;
}

function buildInstructionKeys(instructionDefinition, accounts) {
  return instructionDefinition.accounts.map((account) => {
    const pubkey = accounts[account.name];

    if (!pubkey) {
      throw new Error(`Missing required redeem account: ${account.name}`);
    }

    return {
      pubkey,
      isSigner: account.isSigner,
      isWritable: account.isMut,
    };
  });
}

/**
 * Execute redeem transaction for a finalized game
 * 
 * @param {object} params - Redeem parameters
 * @param {string} params.gamePda - Game state PDA address
 * @param {string} params.reserveVault - Game reserve vault address
 * @param {string} params.mint - Round mint address
 * @param {string} params.tokensIn - Token amount to redeem (raw u64 as string)
 * @returns {Promise<string>} - Transaction signature
 */
export async function redeemGame({ gamePda, reserveVault, mint, tokensIn }) {
  const program = getMayhemProgram();
  const provider = program.getPhantomProvider();
  const walletPublicKey = provider?.publicKey || null;

  if (!provider || !walletPublicKey) {
    throw new Error('Connect a Phantom wallet before redeeming.');
  }

  if (!gamePda) {
    throw new Error('Game PDA is required.');
  }

  if (!reserveVault) {
    throw new Error('Reserve vault is required.');
  }

  if (!mint) {
    throw new Error('Round mint is required.');
  }

  if (!tokensIn || tokensIn === '0') {
    throw new Error('Token amount must be greater than zero.');
  }

  const BigIntValue = window.BigInt;
  const tokensInBigInt = BigIntValue(tokensIn);

  if (tokensInBigInt <= 0n) {
    throw new Error('Token amount must be greater than zero.');
  }

  const instructionDefinition = program.getInstructionDefinition('redeem');

  if (!instructionDefinition) {
    throw new Error('Redeem instruction was not found in the local IDL.');
  }

  const roundMint = new PublicKey(mint);
  const redeemerTokenAccount = deriveAssociatedTokenAddress(
    walletPublicKey,
    roundMint,
    program.associatedTokenProgramId,
    program.tokenProgramId
  );

  const accounts = {
    gameState: new PublicKey(gamePda),
    gameReserveVault: new PublicKey(reserveVault),
    roundMint,
    redeemerTokenAccount,
    redeemer: walletPublicKey,
    tokenProgram: program.tokenProgramId,
  };

  const discriminator = await getInstructionDiscriminator('redeem');
  const data = new Uint8Array([...discriminator, ...encodeU64(tokensInBigInt)]);
  const instruction = new TransactionInstruction({
    programId: program.programId,
    keys: buildInstructionKeys(instructionDefinition, accounts),
    data,
  });

  const { blockhash, lastValidBlockHeight } = await program.connection.getLatestBlockhash('confirmed');
  const transaction = new Transaction({
    feePayer: walletPublicKey,
    recentBlockhash: blockhash,
  }).add(instruction);

  const response = await provider.signAndSendTransaction(transaction);
  const signature = typeof response === 'string' ? response : response.signature;

  await program.connection.confirmTransaction(
    {
      signature,
      blockhash,
      lastValidBlockHeight,
    },
    'confirmed'
  );

  return signature;
}
